/**
 * Difficulty and card-type risk/reward. Ending targets are written for a single nine-scene chapter;
 * longer runs scale them up so a chosen ending is earned over the whole book, not reached by default.
 * Each card type also carries its own swing on top of the outcome it resolves to:
 * Keepsakes are steady, Twists are a gamble, Chaos pays big but can backfire, Rule-breakers refund Ink at a
 * risk, and Counters guard against the narrator without building any ending.
 */
import type { CardDef, EndingDef, Effect, SoloRunState, Track } from '../types.js';

/** Scenes an ending's minValue is written for (one chapter). */
export const TARGET_BASE_SCENES = 9;
/** How much of each extra chapter's length is added to the target (1 = fully linear). */
export const TARGET_RUN_SCALE = 1;

export function runSceneCount(state: Pick<SoloRunState, 'runStartSceneIndex' | 'runEndSceneIndex'>): number {
  return Math.max(1, state.runEndSceneIndex - state.runStartSceneIndex + 1);
}

/** Meter value needed to win an ending in this run (scaled by run length). */
export function endingTarget(
  ending: Pick<EndingDef, 'minValue'>,
  state: Pick<SoloRunState, 'runStartSceneIndex' | 'runEndSceneIndex'>,
): number {
  const n = runSceneCount(state);
  if (n <= TARGET_BASE_SCENES) return ending.minValue;
  return Math.round(ending.minValue * (1 + (n / TARGET_BASE_SCENES - 1) * TARGET_RUN_SCALE));
}

export type SwingKind = 'surge' | 'backfire' | 'steady' | 'guard' | 'refund';

export interface Swing {
  kind: SwingKind;
  /** plain sentence for the What changed panel */
  text: string;
  effects: Effect[];
}

/** Chances per card type (exported so tests and docs agree with the engine). */
export const SWING_ODDS = {
  chaos: { surge: 0.5, backfire: 0.3 },
  twist: { surge: 0.3, backfire: 0.35 },
  rule_breaker: { backfire: 0.35 },
} as const;

/** The ending track an outcome pushed (its largest positive shift), if any. */
function pushedTrack(effects: Effect[]): Track | undefined {
  let best: { t: Track; v: number } | undefined;
  for (const e of effects) {
    if (e.type === 'shift_track' && e.value > 0 && e.target !== 'dread' && (!best || e.value > best.v)) {
      best = { t: e.target as Track, v: e.value };
    }
  }
  return best?.t;
}

/**
 * The card type's swing for one play. Rolls once with rng; returns undefined when nothing extra happens.
 * Effects apply after the outcome's own effects.
 */
export function cardSwing(card: Pick<CardDef, 'type'>, outcomeEffects: Effect[], rng: () => number): Swing | undefined {
  const pushed = pushedTrack(outcomeEffects);
  switch (card.type) {
    case 'chaos': {
      const r = rng();
      if (r < SWING_ODDS.chaos.surge) {
        return { kind: 'surge', text: 'Chaos surges: the crowd loves it, chaos +1 more', effects: [{ type: 'shift_track', target: 'chaos', value: 1 }] };
      }
      if (r >= 1 - SWING_ODDS.chaos.backfire) {
        return { kind: 'backfire', text: 'Chaos backfires: the ink spills, Dread +1 more', effects: [{ type: 'shift_dread', value: 1 }] };
      }
      return undefined;
    }
    case 'twist': {
      if (!pushed) return undefined;
      const r = rng();
      if (r < SWING_ODDS.twist.surge) {
        return { kind: 'surge', text: 'The twist lands hard: +1 more', effects: [{ type: 'shift_track', target: pushed, value: 1 }] };
      }
      if (r >= 1 - SWING_ODDS.twist.backfire) {
        return { kind: 'backfire', text: 'The narrator saw it coming: the twist builds nothing', effects: [{ type: 'shift_track', target: pushed, value: -1 }] };
      }
      return undefined;
    }
    case 'rule_breaker': {
      const r = rng();
      if (r >= 1 - SWING_ODDS.rule_breaker.backfire) {
        return { kind: 'backfire', text: 'The narrator objects to the rule-bending: Dread +1', effects: [{ type: 'shift_dread', value: 1 }] };
      }
      return { kind: 'refund', text: 'Rule bent cleanly: 1 Ink back', effects: [{ type: 'grant_energy', value: 1 }] };
    }
    case 'counter': {
      if (!pushed) return undefined;
      const gain = outcomeEffects.filter((e) => e.type === 'shift_track' && e.target === pushed && e.value > 0).reduce((a, e) => a + (e as { value: number }).value, 0);
      return { kind: 'guard', text: 'Counters guard against the plan but build no ending', effects: [{ type: 'shift_track', target: pushed, value: -gain }] };
    }
    case 'keepsake':
      return { kind: 'steady', text: 'Keepsakes are steady: no risk, no surprises', effects: [] };
    default:
      return undefined;
  }
}

/** One-line risk and reward per card type, shown with the selected card and in the help. */
export const CARD_TYPE_RISK: Record<string, string> = {
  keepsake: 'Keepsake: steady. Builds Kind Hands with no risk and no surprises.',
  twist: 'Twist: a gamble. Sometimes lands hard for +1 more, sometimes the narrator saw it coming and it builds nothing.',
  chaos: 'Chaos: big swings. Adds Dread, often surges for +1 more chaos, sometimes backfires for +1 more Dread.',
  rule_breaker: 'Rule-breaker: usually hands 1 Ink back, but the narrator may object (Dread +1).',
  counter: 'Counter: guards. Blocks the plan and eases Dread, but builds no ending.',
};
